import { ActionTree } from 'vuex';
import ILocalState from './stateInterface';
import { AjaxService } from '@/services/AjaxService';
import { ajaxUrls } from '@/helpers/ajaxUrlsHelper';
import { ProductSystemCodeEntry } from '@/interfaces/ProductSystemCodeEntry';
import { FilteredResultRequest } from '@/interfaces/FilteredResultRequest';
import { CheckoutEntry } from '@/interfaces/CheckoutEntry';
import { ActiveAccount } from '@/interfaces/ActiveAccount';
import { formatDateWithMonthName } from '@/helpers/dateFormatter';
import { BillingAddress } from '@/interfaces/BillingAddress';
import { UserData } from '@/interfaces/UserData';
import { InvoiceReport } from '@/interfaces/InvoiceReport';
import { LoginRecord } from '@/interfaces/LoginRecord';
import { SingleRecordDetailsInterface } from '@/interfaces/SingleRecordDetailsInterface';
import { StatisticalAnalysisInterface } from '@/interfaces/StatisticalAnalysisInterface';
import { LoginUserData } from '@/interfaces/LoginUserData';
import { ProductReport } from '@/interfaces/ProductReport';
import Jsona from 'jsona';
import { TJsonApiBody, TJsonApiData } from 'jsona/lib/JsonaTypes';
import { FilterService } from '@/services/FilterService';
import { FilterData } from '@/interfaces/FilterData';
import {ProductGroupTypes} from '@/enums/ProductGroupTypes';
import * as Sentry from "@sentry/vue";

const dataFormatter = new Jsona();

const actions: ActionTree<ILocalState, {}> = {
    async getShoppingListRecords(context, isCount = false) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let shoppingListRecords: TJsonApiBody;
        try {
            shoppingListRecords =
                await AjaxService.get(ajaxUrls.user.usersShoppingListRecordsWithGroup(
                    context.getters.activeProductGroup, isCount), token) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setShoppingListRecords', shoppingListRecords);
        context.commit('setFilteredShoppingListRecords', context.getters.shoppingListRecords);
        return Promise.resolve();
    },
    async removeShoppingListRecords(context, idsToBeRemoved: number[]) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        for await(const id of idsToBeRemoved) {
            try {
                await AjaxService.delete(ajaxUrls.user.shoppingListWithId(id), token);
            } catch (e) {
                return Promise.reject(e);
            }
        }
        return Promise.resolve();
    },
    async addToShoppingCart(context, idsToShoppingCart: number[]) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        for await(const id of idsToShoppingCart) {
            const shoppingListRecords: ProductReport[] = context.getters.shoppingListRecords;
            const record = shoppingListRecords.filter((singleRecord: ProductReport) => singleRecord.id === id)[0];
            try {
                await AjaxService.post(ajaxUrls.user.usersShoppingCart, {
                    data: {
                        type: 'shopcart_items',
                        attributes: {
                            prodGroup: record.prodGroup,
                            keywords: record.text,
                        },
                        relationships: {
                            brand: {
                                data: {
                                    type: 'brands',
                                    id: record.brand ? record.brand.id : record.id,
                                },
                            },
                        },
                    },
                }, token);
            } catch (e) {
                return Promise.reject(e);
            }
        }
        await context.dispatch('removeShoppingListRecords', idsToShoppingCart);
        await context.dispatch('refreshShoppingCartAndList');
        return Promise.resolve();
    },
    async getShoppingCartRecords(context, isCount = false) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let shoppingCartRecords: TJsonApiData;
        try {
            shoppingCartRecords =
                await AjaxService.get(ajaxUrls.user.usersShoppingCartRecordsWithGroup(
                    context.getters.activeProductGroup, isCount), token) as TJsonApiData;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setShoppingCartRecords', shoppingCartRecords);
        if (shoppingCartRecords.meta) {
            context.commit('setActiveCheckoutMethod', shoppingCartRecords.meta.checkoutMethod);
        }
        return Promise.resolve();
    },
    async revertToShoppingList(context, idsToBeReverted: number[]) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        for await(const id of idsToBeReverted) {
            const shoppingCartRecords: ProductReport[] = context.getters.shoppingCartRecords;
            const record = shoppingCartRecords.filter((singleRecord: ProductReport) => singleRecord.id === id)[0];
            try {
                await AjaxService.post(ajaxUrls.user.usersShoppingList, {
                    data: {
                        type: 'shoplist_items',
                        attributes: {
                            prodGroup: record.prodGroup,
                            keywords: record.text,
                        },
                        relationships: {
                            brand: {
                                data: {
                                    type: 'brands',
                                    id: record.brand ? record.brand.id : record.id,
                                },
                            },
                        },
                    },
                }, token);
            } catch (e) {
                return Promise.reject(e);
            }
        }
        await context.dispatch('removeShoppingCartRecords', idsToBeReverted);
        await context.dispatch('refreshShoppingCartAndList');
        return Promise.resolve();
    },
    async removeShoppingCartRecords(context, idsToBeRemoved: number[]) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        for await (const id of idsToBeRemoved) {
            try {
                await AjaxService.delete(ajaxUrls.user.shoppingCartWithId(id), token);
            } catch (e) {
                return Promise.reject(e);
            }
        }
        return Promise.resolve();
    },
    authorizeUser(context) {
        context.commit('authorizeUser');
    },
    loginUser(context, userData: LoginUserData) {
        return new Promise(async (resolve, reject) => {
            let userLogin: TJsonApiBody | null = null;
            // @ts-ignore
            const deviceId = window.$cookies.isKey('device') ? window.$cookies.get('device') : null;
            try {
                userLogin = await AjaxService.post(ajaxUrls.user.login, {
                    data: {
                        attributes: {
                            email: userData.email,
                            password: userData.password,
                            deviceToken: deviceId,
                        },
                    },
                }, '') as TJsonApiBody;
            } catch (e) {
                if (e.response && e.response.data && e.response.data.errors) {
                    e.response.data.errors.forEach((error: { detail: string, source: { pointer: string } }) => {
                        // @ts-ignore
                        if (error.code === 'UNRECOGNIZED_DEVICE') {
                            // @ts-ignore
                            window.$cookies.set('device', error.meta.deviceToken);
                        }
                    });
                }
                reject(e);
                return;
            }
            if (!userLogin.data) {
                reject(new Error('Token is empty'));
                return;
            }
            const today = new Date();
            // @ts-ignore
            AjaxService.setToken(userLogin.data.attributes.access_token);
            // @ts-ignore
            window.$cookies.set('token',
                // @ts-ignore
                userLogin.data.attributes.access_token, new Date(userLogin.data.attributes.expires_at));
            // @ts-ignore
            await context.dispatch('getUserData', userLogin.data.attributes.access_token);
            // @ts-ignore
            if (userLogin.meta) {
                // @ts-ignore
                window.$cookies.set('device', userLogin.meta.deviceToken);
            }
            resolve();
        });
    },
    registerUser(context, registeredUserData: LoginUserData) {
        return new Promise(async (resolve, reject) => {
            try {
                await AjaxService.post(ajaxUrls.user.register, {
                    data: {
                        type: 'users',
                        attributes: {
                            email: registeredUserData.email,
                            password: registeredUserData.password,
                            agreeTerms: true,
                        },
                    },
                });
            } catch (e) {
                console.error(e);
                reject(e);
                return;
            }
            resolve();
        });
    },
    async confirmUser(context, token: string) {
        try {
            await AjaxService.post(ajaxUrls.user.confirmToken, {
                data: {
                    type: 'token_verifications',
                    attributes: {
                        token,
                    },
                },
            });
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    async confirmDevice(context, token: string) {
        try {
            await AjaxService.post(ajaxUrls.user.confirmDevice, {
                data: {
                    type: 'token_verifications',
                    attributes: {
                        token,
                    },
                },
            });
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    getUserData(context, token) {
        return new Promise(async (resolve, reject) => {
            context.commit('setToken', token);
            await context.dispatch('getMe');
            context.commit('authorizeUser');
            resolve();
        });
    },
    logoutUser(context) {
        AjaxService.unsetToken();
        // @ts-ignore
        window.$cookies.remove('token');
        context.commit('unauthorizeUser');
        context.commit('setShoppingCartRecords', null);
        context.commit('setShoppingListRecords', null);
        context.commit('setSwitchedUserEmail', null);
        context.commit('search/setSearchKeywords', '', {root: true});
        context.commit('logoutUser');
        Sentry.setUser(null);
    },
    async resetPassword(context, email) {
        try {
            await AjaxService.post(ajaxUrls.user.resetPassword, {
                data: {
                    type: 'email_requests',
                    attributes: {
                        email,
                    },
                },
            });
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },

    async changePassword(context, payload) {
        try {
            await AjaxService.post(ajaxUrls.user.changePassword, {
                data: {
                    type: 'password_resets',
                    attributes: {
                        password: payload.password,
                        passwordRepeat: payload.passwordRepeat,
                        token: payload.token,
                    },
                },
            });
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    async getMe(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        const parsedToken = parseJwt(token);
        const userData = await AjaxService.get(ajaxUrls.user.userData(parsedToken.username), token) as UserData;

        Sentry.setUser({ email: userData.email });

        context.commit('setUserData', userData);
    },
    async getCheckoutList(context, id) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let contracts: TJsonApiBody;
        try {
            contracts =
                await AjaxService.get(ajaxUrls.user.contracts(id), token) as TJsonApiBody;

        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setCheckoutList', contracts);
        return Promise.resolve();
    },
    async getProjectBillingsSubscription(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let projectBillingsSubscription: TJsonApiBody;
        try {
            projectBillingsSubscription =
                await AjaxService.get(ajaxUrls.user.projectBillingsSubscription, token) as TJsonApiBody;

        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setProjectBillingsSubscription', projectBillingsSubscription);
        return Promise.resolve();
    },
    async getProjectBillingsCredits(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let projectBillingsCredits: TJsonApiBody;
        try {
            projectBillingsCredits =
                await AjaxService.get(ajaxUrls.user.projectBillingsCredits, token) as TJsonApiBody;

        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setProjectBillingsCredits', projectBillingsCredits);
        return Promise.resolve();
    },
    async getActiveAccount(context) {
        // const activeAccount = await AjaxService.get(ajaxUrls.user.activeAccount) as ActiveAccount;
        // context.commit('setActiveAccount', activeAccount);
    },
    async getBillingAddresses(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let billingAddresses: TJsonApiBody;
        try {
            billingAddresses =
                await AjaxService.get(ajaxUrls.user.billingAddresses, token) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setBillingAddresses', billingAddresses);
        return Promise.resolve();
    },
    async addBillingAddress(context, billingAddressData) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        const country = billingAddressData.attributes.country;
        delete billingAddressData.attributes.country;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            await AjaxService.post(ajaxUrls.user.billingAddresses, {
                data: {
                    type: 'billing_addresses',
                    attributes: billingAddressData.attributes,
                    relationships: {
                        country: {
                            data: {
                                type: 'countries',
                                id: country,
                            },
                        },
                    },
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    async updateBillingAddress(context, billingAddressData) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        const country = billingAddressData.attributes.country;
        delete billingAddressData.attributes.country;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            await AjaxService.patch(ajaxUrls.user.billingAddressesWithId(billingAddressData.id), {
                data: {
                    type: 'billing_addresses',
                    id: billingAddressData.id,
                    attributes: billingAddressData.attributes,
                    relationships: {
                        country: {
                            data: {
                                type: 'countries',
                                id: country,
                            },
                        },
                    },
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    async deleteBillingAddress(context, id: number) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            await AjaxService.delete(ajaxUrls.user.billingAddressesWithId(id), token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    async getBluesnapToken(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let bluesnapToken: TJsonApiBody;
        try {
            bluesnapToken =
                await AjaxService.get(ajaxUrls.user.bluesnapToken, token) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setBluesnapToken', bluesnapToken);
        return Promise.resolve();
    },
    async getInvoices(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let invoices: TJsonApiBody;
        try {
            invoices =
                await AjaxService.get(ajaxUrls.user.invoices, token) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setInvoices', invoices);
        return Promise.resolve();
    },

    async getLoginRecords(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let logins: TJsonApiBody;
        try {
            logins =
                await AjaxService.get(ajaxUrls.user.lastSignIns, token) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setLoginRecords', logins);
        return Promise.resolve();
    },
    async getProjectBillings(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let projectBillings: TJsonApiBody;
        try {
            projectBillings =
                await AjaxService.get(ajaxUrls.user.projectBillings, token) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setProjectBillings', projectBillings);
        return Promise.resolve();
    },
    async getCurrentStatisticalAnalysis(context, ids: string) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let statisticalAnalysis: TJsonApiBody;
        try {
            statisticalAnalysis =
                await AjaxService.get(ajaxUrls.projects.statisticalAnalysis(
                    ids, this.getters['user/activeProductGroup']), token) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setCurrentStatisticalAnalysis', statisticalAnalysis);
        return Promise.resolve();
    },
    setActiveProductGroup(context, productGroupType) {
        return new Promise(async (resolve, reject) => {
            context.commit('setActiveProductGroup', productGroupType);
            if (productGroupType !== ProductGroupTypes.None) {
                await context.dispatch('getShoppingCartRecords', true).catch((e) => {
                    reject(e);
                });
                await context.dispatch('getShoppingListRecords', true);
            }
            await context.dispatch('projects/getMyProjects', productGroupType, {root: true});
            resolve();
        });
    },
    setActiveProductGroupForShoppingCart(context, productGroupType) {
        return new Promise(async (resolve, reject) => {
            if (productGroupType !== ProductGroupTypes.None) {
                await context.dispatch('getShoppingCartRecords')
                    .catch((e) => {
                        reject(e);
                    });
            }
            resolve();
        });
    },
    setActiveProductGroupForShoppingList(context, productGroupType) {
        return new Promise(async (resolve, reject) => {
            if (productGroupType !== ProductGroupTypes.None) {
                await context.dispatch('getShoppingListRecords');
            }
            resolve();
        });
    },
    async uploadProfilePicture(context, image) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        const parsedToken = parseJwt(token);
        const formData = new FormData();
        formData.append('profilePicture', image);
        try {
            await AjaxService.post(await ajaxUrls.user.profilePicture, formData, token);
        } catch (e) {
            return Promise.reject(e);
        }
        context.dispatch('getMe');
        return Promise.resolve();
    },
    async removeProfilePicture(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            await AjaxService.delete(await ajaxUrls.user.profilePicture, token);
        } catch (e) {
            return Promise.reject(e);
        }
        context.dispatch('getMe');
        return Promise.resolve();
    },
    async refreshShoppingCartAndList(context) {
        await context.dispatch('getShoppingCartRecords');
        await context.dispatch('getShoppingListRecords');
    },
    async refreshToken(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        let newToken: string;
        try {
            newToken = await AjaxService.post(ajaxUrls.user.refreshToken, {}, token) as string;
        } catch (e) {
            return Promise.reject(e);
        }
        const today = new Date();
        // @ts-ignore
        AjaxService.setToken(newToken.data.attributes.access_token);
        // @ts-ignore
        window.$cookies.set('token',
            // @ts-ignore
            newToken.data.attributes.access_token, new Date(today.setHours(today.getHours() + 1)).toUTCString());
        return Promise.resolve();
    },
    extractFilterOptions(context) {
        const filterService = new FilterService(context.getters.shoppingListRecords);
        const filterOptions = filterService.getFilterOptions();
        context.commit('search/setFilterOptions', filterOptions, {root: true});
    },
    filterShoppingListRecords(context, filterData: FilterData) {
        return new Promise(async (resolve) => {
            const filterService = new FilterService(context.getters.shoppingListRecords);
            const filteredProductReports = await filterService.filterProductReports(filterData);
            context.commit('setFilteredShoppingListRecords', filteredProductReports);
            resolve();
        });
    },
    async addShoppingListRecord(context, brandId) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        const productSearchResults: ProductReport[] = context.rootGetters['search/productSearchResults'];
        const result = productSearchResults.find((singleRecord: ProductReport) => singleRecord.id === brandId);
        if (!result) {
            return Promise.reject(new Error('This brand id doesn\'t exist'));
        }
        try {
            await AjaxService.post(ajaxUrls.user.usersShoppingList, {
                data: {
                    type: 'shoplist_items',
                    attributes: {
                        prodGroup: context.getters.activeProductGroup,
                        keywords: context.rootGetters['search/searchKeywords'],
                    },
                    relationships: {
                        brand: {
                            data: {
                                type: 'brands',
                                id: result.id,
                            },
                        },
                    },
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    async updateUserData(context, userData) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        const parsedToken = parseJwt(token);
        let response;
        const profileCountry = userData.profileCountry;
        const attributes = userData;
        const relationships = profileCountry ? {
            country: {
                data: {
                    type: 'countries',
                    id: profileCountry,
                },
            },
        } : {};
        delete attributes.profileCountry;
        try {
            await AjaxService.patch(response = await ajaxUrls.user.userData(parsedToken.username), {
                data: {
                    type: 'users',
                    id: parsedToken.username,
                    attributes,
                    relationships,
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        context.dispatch('getMe');
        return Promise.resolve();
    },
    async getCountryList(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let countryList;
        try {
            countryList = await AjaxService.get(ajaxUrls.user.countries, token);
        } catch {
            return Promise.reject();
        }
        context.commit('setCountryList', countryList);
        return Promise.resolve();
    },
    async requestEmailReset(context, emailData) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            await AjaxService.post(ajaxUrls.user.requestEmailReset, {
                data: {
                    type: 'change_email_requests',
                    attributes: {
                        email: emailData.email,
                        emailCanonical: emailData.emailRepeat,
                    },
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    async updatePassword(context, passwordData) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            await AjaxService.post(ajaxUrls.user.updatePassword, {
                data: {
                    type: 'password_updates',
                    attributes: {
                        oldPassword: passwordData.oldPassword,
                        password: passwordData.password,
                        passwordRepeat: passwordData.passwordRepeat,
                    },
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },

    async downloadInvoice(context, id) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let invoice: File;
        try {
            invoice = await AjaxService.get(ajaxUrls.user.invoiceDownload(id), token) as File;
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(invoice);
    },

    async downloadProjectBillings(context, id) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let projectBilling: File;
        try {
            projectBilling = await AjaxService.get(ajaxUrls.user.projectBillingDownload(id), token) as File;
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(projectBilling);
    },

    async getCartSummary(context, payload) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let summary: TJsonApiBody;
        let url = '';
        const params = {
            data: {
                type: 'pay_per_views',
                attributes: {
                    prodGroup: context.getters.activeProductGroup,
                },
                relationships: {},
            },
        };
        if (payload.type === 'buySubscription') {
            url = ajaxUrls.user.orderSummary('buy-subscription');
            params.data.relationships = {
                billingAddress: {
                    data: {
                        type: 'billing_addresses',
                        id: payload.billingAddressId,
                    },
                },
            };
        }
        if (payload.type === 'buyPrepaidCredit') {
            url = ajaxUrls.user.orderSummary('buy-prepaid-credit');
            params.data.relationships = {
                billingAddress: {
                    data: {
                        type: 'billing_addresses',
                        id: payload.billingAddressId,
                    },
                },
            };
        }
        if (payload.type === 'usePrepaid') {
            url = ajaxUrls.user.shopcartSummary('use-prepaid-credit');
        }
        if (payload.type === 'useSubscription') {
            url = ajaxUrls.user.shopcartSummary('use-subscription');

        }
        if (payload.type === 'usePayPerView') {
            url = ajaxUrls.user.shopcartSummary('use-pay-per-view');
            if (payload.billingAddressId != null) {
                params.data.relationships = {
                    billingAddress: {
                        data: {
                            type: 'billing_addresses',
                            id: payload.billingAddressId,
                        },
                    },
                };
            }
        }
        try {
            summary = await AjaxService.post(url, params, token) as TJsonApiBody;
        } catch (e) {
            return Promise.reject(e);
        }
        context.commit('setCartSummary', summary);
        return Promise.resolve(summary);
    },
    async buyUsingSubscriptions(context, projectId) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            await AjaxService.post(ajaxUrls.user.buyUsingSubscriptions, {
                data: {
                    type: 'subscriptions',
                    attributes: {
                        prodGroup: context.getters.activeProductGroup,
                    },
                    relationships: {
                        project: {
                            data: {
                                type: 'projects',
                                id: projectId,
                            },
                        },
                    },
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    async buyUsingCredits(context, projectId) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            await AjaxService.post(ajaxUrls.user.buyUsingCredits, {
                data: {
                    type: 'prepaid_credits',
                    attributes: {
                        prodGroup: context.getters.activeProductGroup,
                    },
                    relationships: {
                        project: {
                            data: {
                                type: 'projects',
                                id: projectId,
                            },
                        },
                    },
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },

    async buyUsingPayPerView(context, payload) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        let response = null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            response = await AjaxService.post(ajaxUrls.user.buyUsingPayPerView, {
                data: {
                    type: 'pay_per_views',
                    attributes: {
                        prodGroup: context.getters.activeProductGroup,
                        paymentClient: payload.paymentMethod,
                        paymentClientIntentId: payload.paymentIntentId,
                        paymentMethodNonce: payload.pfToken ? payload.pfToken : payload.nonce,
                    },
                    relationships: {
                        billingAddress: {
                            data: {
                                type: 'billing_addresses',
                                id: payload.billingAddressId,
                            },
                        },
                        project: {
                            data: {
                                type: 'projects',
                                id: payload.projectId,
                            },
                        },
                    },
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(response);
    },

    async buySubscription(context, payload) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        let response = null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            response = await AjaxService.post(ajaxUrls.user.buySubscription, {
                data: {
                    type: 'pay_per_views',
                    attributes: {
                        paymentClient: payload.paymentMethod,
                        paymentClientIntentId: payload.paymentIntentId,
                        paymentMethodNonce: payload.pfToken ? payload.pfToken : payload.nonce,
                    },
                    relationships: {
                        billingAddress: {
                            data: {
                                type: 'billing_addresses',
                                id: payload.billingAddressId,
                            },
                        },
                    },
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(response);
    },
    async buyCredits(context, payload) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        let response = null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        try {
            response = await AjaxService.post(ajaxUrls.user.buyCredits, {
                data: {
                    type: 'pay_per_views',
                    attributes: {
                        paymentClient: payload.paymentMethod,
                        paymentClientIntentId: payload.paymentIntentId,
                        paymentMethodNonce: payload.pfToken ? payload.pfToken : payload.nonce,
                    },
                    relationships: {
                        billingAddress: {
                            data: {
                                type: 'billing_addresses',
                                id: payload.billingAddressId,
                            },
                        },
                    },
                },
            }, token);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve(response);
    },
    setSwitchedUserEmail(context, payload) {
        context.commit('setSwitchedUserEmail', payload);
    },
    async getShoppingListNumber(context, productGroupType) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let shoppingListRecords: TJsonApiBody;
        try {
            shoppingListRecords =
                await AjaxService.get(ajaxUrls.user.usersShoppingListTotalItems(), token) as TJsonApiBody;

            let trademarksShopListItems = JSON.parse(JSON.stringify(shoppingListRecords));
            trademarksShopListItems.data = trademarksShopListItems.data.filter((item: any) => item.id === ProductGroupTypes.Trademark)[0];
            context.commit('setTrademarksShopListItems', trademarksShopListItems);

            let customerShopListItems = JSON.parse(JSON.stringify(shoppingListRecords));
            customerShopListItems.data = customerShopListItems.data.filter((item: any) => item.id === ProductGroupTypes.CustomerRelations)[0];
            context.commit('setCustomerShopListItems', customerShopListItems);

            let goodwillShopListItems = JSON.parse(JSON.stringify(shoppingListRecords));
            goodwillShopListItems.data = goodwillShopListItems.data.filter((item: any) => item.id === ProductGroupTypes.Goodwill)[0];
            context.commit('setGoodwillShopListItems', goodwillShopListItems);

            let technologyShopListItems = JSON.parse(JSON.stringify(shoppingListRecords));
            technologyShopListItems.data = technologyShopListItems.data.filter((item: any) => item.id === ProductGroupTypes.Technology)[0];
            context.commit('setTechnologyShopListItems', technologyShopListItems);

            let softwareShopListItems = JSON.parse(JSON.stringify(shoppingListRecords));
            softwareShopListItems.data = softwareShopListItems.data.filter((item: any) => item.id === ProductGroupTypes.Software)[0];
            context.commit('setSoftwareShopListItems', softwareShopListItems);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    async getShoppingCartNumber(context, productGroupType) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        let shoppingCartRecords: TJsonApiData;
        try {
            shoppingCartRecords =
                await AjaxService.get(ajaxUrls.user.usersShoppingCartTotalItems(), token) as TJsonApiData;

            let trademarksShopCartItems = JSON.parse(JSON.stringify(shoppingCartRecords));
            trademarksShopCartItems.data = trademarksShopCartItems.data.filter((item: any) => item.id === ProductGroupTypes.Trademark)[0];
            context.commit('setTrademarksShopCartItems', trademarksShopCartItems);

            let customerShopCartItems = JSON.parse(JSON.stringify(shoppingCartRecords));
            customerShopCartItems.data = customerShopCartItems.data.filter((item: any) => item.id === ProductGroupTypes.CustomerRelations)[0];
            context.commit('setCustomerShopCartItems', customerShopCartItems);

            let goodwillShopCartItems = JSON.parse(JSON.stringify(shoppingCartRecords));
            goodwillShopCartItems.data = goodwillShopCartItems.data.filter((item: any) => item.id === ProductGroupTypes.Goodwill)[0];
            context.commit('setGoodwillShopCartItems', goodwillShopCartItems);

            let technologyShopCartItems = JSON.parse(JSON.stringify(shoppingCartRecords));
            technologyShopCartItems.data = technologyShopCartItems.data.filter((item: any) => item.id === ProductGroupTypes.Technology)[0];
            context.commit('setTechnologyShopCartItems', technologyShopCartItems);

            let softwareShopCartItems = JSON.parse(JSON.stringify(shoppingCartRecords));
            softwareShopCartItems.data = softwareShopCartItems.data.filter((item: any) => item.id === ProductGroupTypes.Software)[0];
            context.commit('setSoftwareShopCartItems', softwareShopCartItems);
        } catch (e) {
            return Promise.reject(e);
        }
        return Promise.resolve();
    },
    async getShoppingItemsForAllGroups(context) {
        const token = context.rootGetters['user/token'] ? context.rootGetters['user/token'] : null;
        if (!token) {
            context.dispatch('logoutUser');
            return Promise.reject('Token doesn\'t exist');
        }
        return new Promise(async (resolve, reject) => {
            try {
                await context.dispatch('getShoppingListNumber');
                await context.dispatch('getShoppingCartNumber');
                return resolve();
            } catch (e) {
                return reject(e);
            }
        });
    },
};

function parseJwt(token: string) {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map((c) => {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));

    return JSON.parse(jsonPayload);
}

export default actions;
